import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GuideWrapper,
  Hero,
  BackButton,
  Title,
  Subtitle,
  Container,
  Introduction,
  InteractiveSpecContainer,
  SpecDisplay,
  SpecToken,
  TokenDetailCard,
  SectionTitle,

  TableContainer,
  Table,
  HeroWarningBox,
  CTASection,
  PrimaryButton,
  SecondaryButton,
  TwiImageGroup,
  TwiImageCard,
  LightboxOverlay,
  LightboxContent,
  CloseButton,
  LightboxTitle,
  SpecExplorerContent,
  TyreVisualContainer,
  WheelCardInner,
  SideViewFace,
  FrontViewFace,
  SpecDetailsContainer,
  InfoSection,
  InfoBlock
} from './TyreGuidePage.styles';
import twiShoulderMark from '../images/twi_shoulder_mark.webp';
import twiRealComparison from '../images/twi_real_comparison.webp';



const SPEC_DETAILS = {
  width: {
    title: 'Section Width',
    token: '205',
    color: '#3b82f6',
    activeColor: '59, 130, 246',
    desc: 'The width of the tyre measured in millimetres from sidewall to sidewall. In this example, the tyre has a width of 205 mm.'
  },
  aspect: {
    title: 'Aspect Ratio (Profile)',
    token: '60',
    color: '#e8c547',
    activeColor: '232, 197, 71',
    desc: 'The height of the tyre\'s sidewall expressed as a percentage of the width. Here, "60" means the sidewall height is 60% of 205 mm, which is 123 mm.'
  },
  construction: {
    title: 'Tyre Construction',
    token: 'R',
    color: '#ec4899',
    activeColor: '236, 72, 153',
    desc: 'The "R" stands for Radial construction, indicating a radial tyre—the most common and industry-standard type used for passenger vehicles today.'
  },
  rim: {
    title: 'Rim Diameter',
    token: '16',
    color: '#8b5cf6',
    activeColor: '139, 92, 246',
    desc: 'The diameter of the wheel rim that this tyre is designed to fit, measured in inches. In this case, this tyre must only be mounted on a 16-inch wheel.'
  },
  load: {
    title: 'Load Index',
    token: '92',
    color: '#f97316',
    activeColor: '249, 115, 22',
    desc: 'A numerical code representing the maximum weight (load) capacity a single tyre can safely support. A load index of "92" corresponds to 630 kg per tyre. Across four tyres, they can theoretically support: 630 kg × 4 = 2520 kg.'
  },
  speed: {
    title: 'Speed Symbol',
    token: 'H',
    color: '#ef4444',
    activeColor: '239, 68, 68',
    desc: 'An alphabetical letter representing the maximum approved speed capability of the tyre under its recommended load. "H" corresponds to a maximum approved speed of 210 km/h.'
  }
};


const TyreGuidePage = () => {
  const navigate = useNavigate();
  const [activeToken, setActiveToken] = useState('width');
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    if (lightboxImage) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          setLightboxImage(null);
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [lightboxImage]);

  const currentDetail = SPEC_DETAILS[activeToken];

  return (
    <GuideWrapper>
      <Hero>
        <div className="back-btn-container">
          <BackButton onClick={() => navigate('/tyres')}>
            ← Back to Tyres
          </BackButton>
        </div>
        <Title>How To Read A Car Tyre</Title>
        <Subtitle>
          Demystifying the numbers, letters, and symbols on your tyre sidewall to help you choose the right fit for your vehicle.
        </Subtitle>
        <HeroWarningBox>
          <div className="warning-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" fill="rgba(239, 68, 68, 0.2)" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="warning-text">
            <strong>Safety Note:</strong> Tyres older than 4 years should be regularly inspected, and tyres older than 5 years should be replaced, regardless of remaining tread.
          </div>
        </HeroWarningBox>
      </Hero>

      <Container>
        <Introduction>
          <p>
            Tyres are the only contact point between your vehicle and the road. They are critical to your vehicle's braking, steering, suspension, and overall safety. Knowing how to read the sidewall markings will ensure you remain compliant with safety standards and find the ideal balance of performance, load rating, and speed capacity.
          </p>
        </Introduction>

        <InteractiveSpecContainer>
          <h2>Interactive Tyre Spec Explorer</h2>
          <p style={{ color: '#a1a1aa', marginBottom: '30px', fontSize: '0.95rem' }}>
            Click directly on the tyre markings or use the parameters panel to explore their meaning:
          </p>

          <SpecExplorerContent>
            {/* Left: The Visual SVG Tyre */}
            <TyreVisualContainer>
              <WheelCardInner isFlipped={activeToken === 'width'}>
                <SideViewFace>
                  <svg viewBox="0 0 400 400" width="100%" height="100%">
                    <defs>
                      <marker id="arrow-blue" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#3b82f6" />
                      </marker>
                      <marker id="arrow-yellow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#e8c547" />
                      </marker>
                      <marker id="arrow-purple" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#8b5cf6" />
                      </marker>
                      <marker id="arrow-orange" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#f97316" />
                      </marker>

                      <radialGradient id="treadGrad" cx="50%" cy="50%" r="50%">
                        <stop offset="90%" stopColor="#121212" />
                        <stop offset="96%" stopColor="#1e1e1e" />
                        <stop offset="100%" stopColor="#0d0d0d" />
                      </radialGradient>

                      <radialGradient id="sidewallGrad" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#2c2d30" />
                        <stop offset="65%" stopColor="#1c1d1f" />
                        <stop offset="90%" stopColor="#131415" />
                        <stop offset="100%" stopColor="#0a0a0b" />
                      </radialGradient>

                      <radialGradient id="innerSidewallGrad" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#17181a" />
                        <stop offset="85%" stopColor="#111213" />
                        <stop offset="100%" stopColor="#080809" />
                      </radialGradient>

                      <linearGradient id="rimGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#8a9094" />
                        <stop offset="30%" stopColor="#d1d5db" />
                        <stop offset="50%" stopColor="#4b5563" />
                        <stop offset="70%" stopColor="#f3f4f6" />
                        <stop offset="100%" stopColor="#374151" />
                      </linearGradient>

                      <linearGradient id="spokeGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#9ca3af" />
                        <stop offset="55%" stopColor="#4b5563" />
                        <stop offset="100%" stopColor="#1f2937" />
                      </linearGradient>
                    </defs>

                    {/* Outer Tyre Tread */}
                    <circle cx="200" cy="200" r="185" fill="url(#treadGrad)" stroke="#1a1a1a" strokeWidth="2" />
                    <circle cx="200" cy="200" r="180" fill="none" stroke="#050505" strokeWidth="10" strokeDasharray="10,12" />
                    <circle cx="200" cy="200" r="174" fill="none" stroke="#252525" strokeWidth="2" />
                    
                    {/* Sidewall */}
                    <circle cx="200" cy="200" r="170" fill="url(#sidewallGrad)" />
                    <circle cx="200" cy="200" r="122" fill="url(#innerSidewallGrad)" />

                    {/* Outer Rim Lip */}
                    <circle cx="200" cy="200" r="95" fill="none" stroke="url(#rimGrad)" strokeWidth="6" />
                    <circle cx="200" cy="200" r="92" fill="#151719" stroke="#0a0b0c" strokeWidth="2" />

                    {/* Hub (Center) */}
                    <circle cx="200" cy="200" r="22" fill="#24272a" stroke="url(#rimGrad)" strokeWidth="2" />
                    <circle cx="200" cy="200" r="8" fill="#0c0d0e" />

                    {/* Spoke Layout (5 splits) */}
                    <g id="spokes">
                      {[0, 72, 144, 216, 288].map(deg => (
                        <g key={deg} transform={`rotate(${deg} 200 200)`}>
                          <path d="M 193,178 L 180,108 L 187,108 L 198,178 Z" fill="url(#spokeGrad)" opacity="0.95" />
                          <path d="M 207,178 L 220,108 L 213,108 L 202,178 Z" fill="url(#spokeGrad)" opacity="0.95" />
                        </g>
                      ))}
                    </g>

                    {/* Lug Nuts */}
                    {[0, 72, 144, 216, 288].map(deg => {
                      const rad = (deg * Math.PI) / 180;
                      return (
                        <circle
                          key={deg}
                          cx={200 + 14 * Math.sin(rad)}
                          cy={200 - 14 * Math.cos(rad)}
                          r="3"
                          fill="#e5e7eb"
                          stroke="#374151"
                          strokeWidth="1"
                        />
                      );
                    })}

                    {/* Static Tyre Details (Decorative) */}
                    <g transform="translate(200, 200) rotate(-135) translate(0, -145)">
                      <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="#3f4550" fontSize="7" fontWeight="600">
                        TUBELESS
                      </text>
                    </g>
                    <g transform="translate(200, 200) rotate(135) translate(0, -145)">
                      <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="#3f4550" fontSize="7" fontWeight="600">
                        MADE IN INDIA
                      </text>
                    </g>
                    <g transform="translate(200, 200) rotate(100) translate(0, -145)">
                      <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" fill="#3f4550" fontSize="6" fontWeight="600">
                        MAX PRESS. 44 PSI
                      </text>
                    </g>

                    {/* Dynamic Spec Highlight Overlays */}
                    {activeToken === 'aspect' && (
                      <g className="dim-line">
                        <line x1="15" y1="200" x2="15" y2="250" stroke="#e8c547" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.6" />
                        <line x1="105" y1="200" x2="105" y2="250" stroke="#e8c547" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.6" />
                        <line x1="22" y1="245" x2="98" y2="245" stroke="#e8c547" strokeWidth="2.5" markerStart="url(#arrow-yellow)" markerEnd="url(#arrow-yellow)" />
                        <rect x="15" y="255" width="90" height="20" rx="3" fill="#0b0f19" stroke="#e8c547" strokeWidth="1" />
                        <text x="60" y="265" textAnchor="middle" dominantBaseline="middle" fill="#e8c547" fontSize="10" fontWeight="700">
                          60% of 205 (123mm)
                        </text>
                      </g>
                    )}

                    {activeToken === 'construction' && (
                      <g className="dim-line">
                        {[195, 210, 225, 240, 255, 270].map(ang => {
                          const rad = (ang * Math.PI) / 180;
                          return (
                            <line
                              key={ang}
                              x1={200 + 95 * Math.sin(rad)}
                              y1={200 - 95 * Math.cos(rad)}
                              x2={200 + 185 * Math.sin(rad)}
                              y2={200 - 185 * Math.cos(rad)}
                              stroke="#ec4899"
                              strokeWidth="3"
                              strokeDasharray="4 4"
                              opacity="0.8"
                            />
                          );
                        })}
                        <rect x="50" y="295" width="100" height="20" rx="3" fill="#0b0f19" stroke="#ec4899" strokeWidth="1" />
                        <text x="100" y="305" textAnchor="middle" dominantBaseline="middle" fill="#ec4899" fontSize="10" fontWeight="700">
                          Radial Casing
                        </text>
                      </g>
                    )}

                    {activeToken === 'rim' && (
                      <g className="dim-line">
                        <line x1="133" y1="267" x2="267" y2="133" stroke="#8b5cf6" strokeWidth="2.5" markerStart="url(#arrow-purple)" markerEnd="url(#arrow-purple)" />
                        <rect x="175" y="188" width="50" height="24" rx="4" fill="#0b0f19" stroke="#8b5cf6" strokeWidth="1.5" />
                        <text x="200" y="200" textAnchor="middle" dominantBaseline="middle" fill="#8b5cf6" fontSize="12" fontWeight="800">
                          16" Rim
                        </text>
                      </g>
                    )}

                    {activeToken === 'load' && (
                      <g className="dim-line">
                        <line x1="100" y1="388" x2="300" y2="388" stroke="#a1a1aa" strokeWidth="2" opacity="0.6" />
                        <path d="M 200,200 L 200,380" stroke="#f97316" strokeWidth="3" markerEnd="url(#arrow-orange)" />
                        <rect x="135" y="145" width="130" height="26" rx="4" fill="#0b0f19" stroke="#f97316" strokeWidth="1.5" />
                        <text x="200" y="158" textAnchor="middle" dominantBaseline="middle" fill="#f97316" fontSize="11" fontWeight="800">
                          LOAD CAP. 630 kg
                        </text>
                      </g>
                    )}

                    {activeToken === 'speed' && (
                      <g className="dim-line">
                        <path d="M 390,150 A 190,190 0 0,1 390,250" stroke="#ef4444" strokeWidth="3.5" fill="none" strokeDasharray="15 15" opacity="0.8" />
                        <path d="M 10,250 A 190,190 0 0,1 10,150" stroke="#ef4444" strokeWidth="3.5" fill="none" strokeDasharray="15 15" opacity="0.8" />
                        <rect x="135" y="230" width="130" height="26" rx="4" fill="#0b0f19" stroke="#ef4444" strokeWidth="1.5" />
                        <text x="200" y="243" textAnchor="middle" dominantBaseline="middle" fill="#ef4444" fontSize="11" fontWeight="800">
                          MAX 210 km/h
                        </text>
                      </g>
                    )}

                    {/* Interactive Sidewall Markings */}
                    {[
                      { key: 'width', text: '205', angle: -45, color: '#3b82f6' },
                      { key: 'slash', text: '/', angle: -30, color: '#5a5e66', isStatic: true },
                      { key: 'aspect', text: '60', angle: -18, color: '#e8c547' },
                      { key: 'construction', text: 'R', angle: -5, color: '#ec4899' },
                      { key: 'rim', text: '16', angle: 8, color: '#8b5cf6' },
                      { key: 'space', text: ' ', angle: 16, isStatic: true },
                      { key: 'load', text: '92', angle: 24, color: '#f97316' },
                      { key: 'speed', text: 'H', angle: 38, color: '#ef4444' }
                    ].map((m) => {
                      if (m.isStatic) {
                        if (m.text === ' ') return null;
                        return (
                          <g key={m.key} transform={`translate(200, 200) rotate(${m.angle}) translate(0, -145)`}>
                            <text
                              x="0"
                              y="0"
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fill="#5a5e66"
                              fontSize="18"
                              fontWeight="800"
                              style={{ pointerEvents: 'none' }}
                            >
                              {m.text}
                            </text>
                          </g>
                        );
                      }

                      const isActive = activeToken === m.key;
                      return (
                        <g
                          key={m.key}
                          transform={`translate(200, 200) rotate(${m.angle}) translate(0, -145)`}
                          onClick={() => setActiveToken(m.key)}
                          style={{ cursor: 'pointer' }}
                        >
                          {isActive && (
                            <circle
                              cx="0"
                              cy="0"
                              r="18"
                              fill={m.color}
                              opacity="0.18"
                              className="dim-line"
                            />
                          )}
                          <text
                            x="0"
                            y="0"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="18"
                            fontWeight="800"
                            fill={isActive ? m.color : '#a1a1aa'}
                            style={{
                              transition: 'all 0.3s ease',
                              filter: isActive ? `drop-shadow(0 0 6px ${m.color})` : 'none',
                            }}
                            className="interactive-marking"
                          >
                            {m.text}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </SideViewFace>
                <FrontViewFace onClick={() => setActiveToken('aspect')}>
                  <svg viewBox="0 0 400 400" width="100%" height="100%">
                    <defs>
                      <linearGradient id="treadFrontGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#0a0a0a" />
                        <stop offset="15%" stopColor="#1e1e21" />
                        <stop offset="50%" stopColor="#2e2f33" />
                        <stop offset="85%" stopColor="#1e1e21" />
                        <stop offset="100%" stopColor="#0a0a0a" />
                      </linearGradient>
                      <linearGradient id="shadowGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgba(0,0,0,0.6)" />
                        <stop offset="100%" stopColor="rgba(0,0,0,0)" />
                      </linearGradient>
                      <marker id="arrow-blue-front" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M 0 0 L 10 5 L 0 10 z" fill="#3b82f6" />
                      </marker>
                    </defs>

                    <ellipse cx="200" cy="385" rx="90" ry="12" fill="url(#shadowGrad)" opacity="0.8" />

                    {/* Tyre Body (Front Profile) - shifted down slightly to make room for top dimensions and labels */}
                    <rect x="130" y="60" width="140" height="320" rx="28" fill="url(#treadFrontGrad)" stroke="#111" strokeWidth="3" />
                    <rect x="132" y="62" width="136" height="316" rx="26" fill="none" stroke="#0a0a0b" strokeWidth="2" opacity="0.4" />

                    {/* Grooves */}
                    <line x1="200" y1="60" x2="200" y2="380" stroke="#000" strokeWidth="6" />
                    <line x1="165" y1="60" x2="165" y2="380" stroke="#000" strokeWidth="4" />
                    <line x1="235" y1="60" x2="235" y2="380" stroke="#000" strokeWidth="4" />

                    {/* Sipes */}
                    {Array.from({ length: 15 }).map((_, i) => {
                      const yOffset = 72 + i * 20;
                      return (
                        <g key={i} stroke="#000" strokeWidth="3" fill="none" opacity="0.8">
                          <path d={`M 130,${yOffset} Q 145,${yOffset + 5} 150,${yOffset}`} />
                          <path d={`M 270,${yOffset} Q 255,${yOffset + 5} 250,${yOffset}`} />
                          <path d={`M 175,${yOffset + 7} L 190,${yOffset + 12}`} />
                          <path d={`M 225,${yOffset + 7} L 210,${yOffset + 12}`} />
                        </g>
                      );
                    })}

                    {/* Top dimension line (Section Width 205) */}
                    <line x1="130" y1="60" x2="130" y2="25" stroke="rgba(255, 255, 255, 0.4)" strokeWidth="1.5" />
                    <line x1="270" y1="60" x2="270" y2="25" stroke="rgba(255, 255, 255, 0.4)" strokeWidth="1.5" />
                    <line x1="130" y1="35" x2="270" y2="35" stroke="#3b82f6" strokeWidth="2.5" markerStart="url(#arrow-blue-front)" markerEnd="url(#arrow-blue-front)" />
                    <rect x="180" y="23" width="40" height="24" rx="4" fill="#0b0f19" stroke="#3b82f6" strokeWidth="1.5" />
                    <text 
                      x="200" 
                      y="35" 
                      textAnchor="middle" 
                      dominantBaseline="central" 
                      fill="#ffffff" 
                      fontSize="13" 
                      fontWeight="900"
                      style={{ filter: 'drop-shadow(0 0 3px #3b82f6)', fontFamily: "'Outfit', sans-serif" }}
                    >
                      205
                    </text>

                    {/* Highlighted SECTION WIDTH Label directly above the 205 dimension box */}
                    <text 
                      x="200" 
                      y="10" 
                      textAnchor="middle" 
                      dominantBaseline="central" 
                      fill="#3b82f6" 
                      fontSize="12" 
                      fontWeight="900" 
                      style={{ letterSpacing: '1.5px', fontFamily: "'Outfit', sans-serif", filter: 'drop-shadow(0 0 5px rgba(59, 130, 246, 0.5))' }}
                    >
                      SECTION WIDTH
                    </text>
                  </svg>
                </FrontViewFace>
              </WheelCardInner>
            </TyreVisualContainer>

            {/* Right: The Text spec and detailed explanation */}
            <SpecDetailsContainer>
              <SpecDisplay style={{ marginBottom: '20px', justifyContent: 'flex-start' }}>
                <SpecToken
                  color={SPEC_DETAILS.width.color}
                  active={activeToken === 'width' ? SPEC_DETAILS.width.activeColor : null}
                  onClick={() => setActiveToken('width')}
                >
                  {SPEC_DETAILS.width.token}
                </SpecToken>
                <span>/</span>
                <SpecToken
                  color={SPEC_DETAILS.aspect.color}
                  active={activeToken === 'aspect' ? SPEC_DETAILS.aspect.activeColor : null}
                  onClick={() => setActiveToken('aspect')}
                >
                  {SPEC_DETAILS.aspect.token}
                </SpecToken>

                <SpecToken
                  color={SPEC_DETAILS.construction.color}
                  active={activeToken === 'construction' ? SPEC_DETAILS.construction.activeColor : null}
                  onClick={() => setActiveToken('construction')}
                >
                  {SPEC_DETAILS.construction.token}
                </SpecToken>
                <SpecToken
                  color={SPEC_DETAILS.rim.color}
                  active={activeToken === 'rim' ? SPEC_DETAILS.rim.activeColor : null}
                  onClick={() => setActiveToken('rim')}
                >
                  {SPEC_DETAILS.rim.token}
                </SpecToken>
                <span style={{ margin: '0 4px' }}> </span>
                <SpecToken
                  color={SPEC_DETAILS.load.color}
                  active={activeToken === 'load' ? SPEC_DETAILS.load.activeColor : null}
                  onClick={() => setActiveToken('load')}
                >
                  {SPEC_DETAILS.load.token}
                </SpecToken>
                <SpecToken
                  color={SPEC_DETAILS.speed.color}
                  active={activeToken === 'speed' ? SPEC_DETAILS.speed.activeColor : null}
                  onClick={() => setActiveToken('speed')}
                >
                  {SPEC_DETAILS.speed.token}
                </SpecToken>
              </SpecDisplay>

              {currentDetail && (
                <TokenDetailCard borderColor={currentDetail.color} headerColor={currentDetail.color} style={{ margin: '0', maxWidth: '100%', width: '100%' }}>
                  <h3>{currentDetail.title}</h3>
                  <p>{currentDetail.desc}</p>
                </TokenDetailCard>
              )}
            </SpecDetailsContainer>
          </SpecExplorerContent>
        </InteractiveSpecContainer>



        <SectionTitle>Load Index Reference Table</SectionTitle>
        <p style={{ color: '#a1a1aa', lineHeight: 1.6 }}>
          The load index is a numerical code representing the maximum weight (load) a single tyre can safely support when inflated to its maximum pressure.
        </p>
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Load Index</th>
                <th>Max Load (kg)</th>
                <th>Load Index</th>
                <th>Max Load (kg)</th>
                <th>Load Index</th>
                <th>Max Load (kg)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>80</strong></td>
                <td>450 kg</td>
                <td><strong>90</strong></td>
                <td>600 kg</td>
                <td><strong>100</strong></td>
                <td>800 kg</td>
              </tr>
              <tr>
                <td><strong>81</strong></td>
                <td>462 kg</td>
                <td><strong>91</strong></td>
                <td>615 kg</td>
                <td><strong>101</strong></td>
                <td>825 kg</td>
              </tr>
              <tr>
                <td><strong>82</strong></td>
                <td>475 kg</td>
                <td><strong>92</strong></td>
                <td>630 kg</td>
                <td><strong>102</strong></td>
                <td>850 kg</td>
              </tr>
              <tr>
                <td><strong>83</strong></td>
                <td>487 kg</td>
                <td><strong>93</strong></td>
                <td>650 kg</td>
                <td><strong>103</strong></td>
                <td>875 kg</td>
              </tr>
              <tr>
                <td><strong>84</strong></td>
                <td>500 kg</td>
                <td><strong>94</strong></td>
                <td>670 kg</td>
                <td><strong>104</strong></td>
                <td>900 kg</td>
              </tr>
              <tr>
                <td><strong>85</strong></td>
                <td>515 kg</td>
                <td><strong>95</strong></td>
                <td>690 kg</td>
                <td><strong>105</strong></td>
                <td>925 kg</td>
              </tr>
              <tr>
                <td><strong>86</strong></td>
                <td>530 kg</td>
                <td><strong>96</strong></td>
                <td>710 kg</td>
                <td><strong>106</strong></td>
                <td>950 kg</td>
              </tr>
              <tr>
                <td><strong>87</strong></td>
                <td>545 kg</td>
                <td><strong>97</strong></td>
                <td>730 kg</td>
                <td><strong>107</strong></td>
                <td>975 kg</td>
              </tr>
              <tr>
                <td><strong>88</strong></td>
                <td>560 kg</td>
                <td><strong>98</strong></td>
                <td>750 kg</td>
                <td><strong>108</strong></td>
                <td>1000 kg</td>
              </tr>
              <tr>
                <td><strong>89</strong></td>
                <td>580 kg</td>
                <td><strong>99</strong></td>
                <td>775 kg</td>
                <td><strong>109</strong></td>
                <td>1030 kg</td>
              </tr>
            </tbody>
          </Table>
        </TableContainer>

        <SectionTitle>Speed Rating Reference Table</SectionTitle>
        <p style={{ color: '#a1a1aa', lineHeight: 1.6 }}>
          Always fit tyres with a speed rating equal to or higher than the original equipment (OE) specified by your vehicle's manufacturer.
        </p>
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Rating Letter</th>
                <th>Maximum Speed (mph)</th>
                <th>Maximum Speed (km/h)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Q</td>
                <td>99 mph</td>
                <td>160 km/h</td>
              </tr>
              <tr>
                <td>R</td>
                <td>106 mph</td>
                <td>170 km/h</td>
              </tr>
              <tr>
                <td>S</td>
                <td>112 mph</td>
                <td>180 km/h</td>
              </tr>
              <tr>
                <td>T</td>
                <td>118 mph</td>
                <td>190 km/h</td>
              </tr>
              <tr>
                <td>H</td>
                <td>130 mph</td>
                <td>210 km/h</td>
              </tr>
              <tr>
                <td>V</td>
                <td>149 mph</td>
                <td>240 km/h</td>
              </tr>
              <tr>
                <td>W</td>
                <td>168 mph</td>
                <td>270 km/h</td>
              </tr>
              <tr>
                <td>Y</td>
                <td>186 mph</td>
                <td>300 km/h</td>
              </tr>
              <tr>
                <td>(Y)</td>
                <td>&gt; 186 mph</td>
                <td>&gt; 300 km/h</td>
              </tr>
            </tbody>
          </Table>
        </TableContainer>

        <SectionTitle>Additional Sidewall Markings</SectionTitle>
        <InfoSection>
          <InfoBlock>
            <h3>Manufacture Date (DOT Code)</h3>
            <p>
              Look for a code starting with the letters <strong>DOT</strong> on the sidewall. The last 4 digits indicate the exact week and year of manufacture.
            </p>
            <p>
              For example, <strong>XXXX XXXX 1323</strong> means:
            </p>
            <ul>
              <li><strong>13:</strong> Manufactured in the 13th week of the year.</li>
              <li><strong>23:</strong> Manufactured in the year 2023.</li>
            </ul>

          </InfoBlock>

          <InfoBlock>
            <h3>Run Flat Technologies (RFT)</h3>
            <p>
              Run Flat tyres are designed with reinforced sidewalls to support the vehicle's weight in the event of a total loss of air pressure. This allows you to drive safely to a workshop.
            </p>

            <p style={{ fontStyle: 'italic' }}>
              Note: Run flat tyres should only be driven for up to 80 km at a maximum speed of 80 km/h after a puncture.
            </p>
          </InfoBlock>


        </InfoSection>



        <SectionTitle>Tread Wear Indicators (TWI)</SectionTitle>
        <p style={{ color: '#a1a1aa', lineHeight: 1.7, marginBottom: '30px', fontSize: '1.02rem' }}>
          Tread Wear Indicators (TWI) are small raised rubber bars (1.6 mm inside main grooves) that serve as a critical safety limit: once the tyre tread wears down flush with these indicator bars, the tyre is no longer safe to drive and must be replaced immediately. You can easily locate these bars by looking for the <strong>"TWI"</strong> letters or small triangle marks (<strong>&Delta;</strong>) stamped on the tyre sidewall.
        </p>
        <TwiImageGroup>
          <TwiImageCard
            lightBg={false}
            onClick={() => setLightboxImage({ src: twiShoulderMark, title: 'TWI Location Mark on Sidewall' })}
          >
            <div className="image-title">TWI Location Mark on Sidewall</div>
            <img src={twiShoulderMark} alt="Tread Wear Indicator (TWI) Location Mark on Tyre Sidewall" />
          </TwiImageCard>
          <TwiImageCard
            lightBg={false}
            onClick={() => setLightboxImage({ src: twiRealComparison, title: 'TWI on New vs. Worn Out Tyre' })}
          >
            <div className="image-title">TWI on New vs. Worn Out Tyre</div>
            <img src={twiRealComparison} alt="Comparison of Tread Wear Indicator (TWI) on a New Tyre and Worn Out Tyre" />
          </TwiImageCard>
        </TwiImageGroup>

        {lightboxImage && (
          <LightboxOverlay onClick={() => setLightboxImage(null)}>
            <LightboxContent onClick={(e) => e.stopPropagation()}>
              <img src={lightboxImage.src} alt={lightboxImage.title} />
              <LightboxTitle>{lightboxImage.title}</LightboxTitle>
              <CloseButton onClick={() => setLightboxImage(null)}>&times;</CloseButton>
            </LightboxContent>
          </LightboxOverlay>
        )}






        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', margin: '40px 0 0 0', flexWrap: 'wrap' }}>
          <SecondaryButton onClick={() => navigate('/tyres/tyre-care-guide')} style={{ borderColor: 'var(--accentGold)', color: 'var(--accentGold)' }}>
            Explore Tyre Care & Maintenance Guide →
          </SecondaryButton>
        </div>

        <CTASection>
          <h3>Need Professional Tyre Advice or Deals?</h3>
          <p>
            Jindal Tyres is Nagpur's premier tyre dealer. We supply authorized tyres from JK Tyre, Bridgestone, and other leading brands. Our workshop features advanced 3D wheel alignment, computerised balancing, automated fitting, and a convenient car pickup and drop facility.
          </p>
          <div className="cta-buttons">
            <PrimaryButton href="tel:7020847854">
              Call Tyre Experts
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate('/tyres')}>
              Explore Services
            </SecondaryButton>
          </div>
        </CTASection>
      </Container>
    </GuideWrapper>
  );
};

export default TyreGuidePage;
